// Retelling Markdown round-trip — the parser/serializer pair the seed (phase 4)
// and the Markdown export (phase 7) both depend on. They are inverse functions
// over the BODY: `serializeRetelling(parseRetelling(md, config), config) === md`
// for every rewrite file the parser accepts whose heading is already canonical,
// not merely for canonically formatted bodies (blank-line-separated paragraph
// blocks + a final newline). Where the parser tolerates a body deviation it
// records it, so the serializer can reproduce it — tolerating an input you
// cannot write back is the lossy parse this invariant exists to catch.
//
// The HEADING is the deliberate exception: it is REGENERATED from config
// (`divisionLabel`), never echoed from the source. The database stores only the
// arabic chapter number, so the export, the corpus build and the reader's
// flattener all have nothing but that number to rebuild a heading from; a
// parser that preserved the source's own spelling would round-trip files
// beautifully and then disagree with every one of them. Generating instead
// makes a stale or non-canonical label a REPLACEMENT rather than a deviation
// the pipeline carries forever.
//
// Shared (convex/model/) so the Convex export mutation and the ingest scripts
// use one implementation. Pure string processing — no Markdown AST, because the
// stored block text is kept VERBATIM (emphasis markers like `*Peter*` survive in
// `Block.text`; they are interpreted only when flattening to the reader
// DocumentModel, which is what keeps the body round-trip exact).
//
// Everything here is about the HEADING — `# {division label}: {title}`, the one
// part of a chapter that front/back matter has no counterpart for. The body
// rules it shares with `matterMarkdown.ts` live in `blockMarkdown.ts`; see that
// file for why they are extracted rather than copied.

import { blockValidator, type Block } from "./block";
import {
  blockId,
  joinDocument,
  parseBlocks,
  splitDocument,
} from "./blockMarkdown";
import type { BookConfig, HeadingNumeral } from "./bookConfig";

/** A parsed retelling chapter: heading fields plus ordered paragraph blocks. */
export interface ParsedRetelling {
  /** Chapter (peter_pan) / book (odyssey) number from the heading. */
  chapterNumber: number;
  /**
   * Heading title text after the `N:` — the prose title in both books
   * (`"Peter Breaks Through"`, `"A Welcomed Guest"`). NOT the rhapsody letter:
   * in the odyssey the letter is the number and precedes the colon.
   */
  title: string;
  /**
   * Paragraph blocks in order, IDs minted `b001`, `b002`, … deterministically
   * (the cross-phase block-ID contract). The heading is NOT a block — it lives
   * in `chapterNumber`/`title`, so `b001` is always the first paragraph, exactly
   * as `scripts/align.ts` mints them.
   */
  blocks: Block[];
  /**
   * Whether the source file ended with a final newline. Canonical rewrites do,
   * and omitting the field means "yes" — Markdown regenerated from the
   * database is always canonical. Missing it is common in real content, not
   * an edge case: two odyssey rewrites (Books 14 and 17) and thirteen of
   * heidi's fourteen rewrites (all but chapter 4) end without one. The parser
   * already accepts them, so it records the fact instead of letting the
   * serializer invent a byte the source never had. Purely cosmetic: the
   * trailing newline is stripped before the blocks are split, so it touches
   * no block text and no `model.text` offset.
   */
  trailingNewline?: boolean;
}

/**
 * The 24 Homeric rhapsody letters in book order, α (Book 1) … ω (Book 24).
 * Note the medial sigma σ at 18 — the source uses σ, never the final form ς.
 * The odyssey source tree keys off this same sequence for its original chapter
 * filenames (`chapters/chapter_18_σ.md`), so the mapping is not a retells
 * invention.
 */
const RHAPSODY_LETTERS = [..."αβγδεζηθικλμνξοπρστυφχψω"];

/**
 * `# Chapter 1: Peter Breaks Through` / `# Book α: A Welcomed Guest` /
 * `# Letter 3: Sailing On Past Drifting Ice` → division label + title.
 *
 * The label is captured loosely (everything before the FIRST colon) and
 * resolved by `chapterNumberFromLabel`, so a heading whose division is
 * unreadable fails with a message naming the label rather than a bare "did not
 * match". Non-greedy so a title may itself contain a colon — the first colon
 * always ends the label, exactly as the previous `[^:]+` numeral did.
 */
const HEADING_RE = /^# (.+?): (.*)$/;

/**
 * Resolve a heading's numeral token to its chapter number, or `null` when the
 * token is not a legal numeral for the book. Arabic is always legal; a
 * "rhapsody" book additionally accepts the letters α…ω.
 */
export function chapterNumberFromNumeral(
  numeral: string,
  scheme: HeadingNumeral,
): number | null {
  if (/^\d+$/.test(numeral)) return Number(numeral);
  if (scheme !== "rhapsody") return null;
  const i = RHAPSODY_LETTERS.indexOf(numeral);
  return i === -1 ? null : i + 1;
}

/**
 * The canonical numeral for `chapterNumber` under the book's notation. Falls
 * back to arabic for a rhapsody book numbered past ω, which cannot happen for
 * the 24-rhapsody Odyssey but keeps the function total.
 */
export function chapterHeadingNumeral(
  config: BookConfig,
  chapterNumber: number,
): string {
  const letter =
    config.retellingHeadingNumeral === "rhapsody"
      ? RHAPSODY_LETTERS[chapterNumber - 1]
      : undefined;
  return letter ?? String(chapterNumber);
}

/**
 * The canonical division label for `chapterNumber` — everything a heading puts
 * before the colon (`"Chapter 1"`, `"Book α"`, `"Letter 3"`).
 *
 * GENERATED, NEVER ECHOED. The label is derived from config and REPLACES
 * whatever a source file happened to write, rather than being preserved
 * verbatim: the database stores only the arabic chapter number, so every path
 * that regenerates Markdown (the admin export, the corpus build, the reader's
 * flattener) has nothing but the number to work from, and a parser that echoed
 * the source's spelling would disagree with them. That makes this the single
 * definition of how a division is named — see `serializeRetelling`.
 *
 * `divisionLabels` covers books whose divisions do not simply run
 * `{noun} 1…N`: frankenstein opens with four LETTERS and then restarts at
 * Chapter 1, so its sequential chapter 11 is labelled `"Chapter 7"`.
 */
export function divisionLabel(
  config: BookConfig,
  chapterNumber: number,
): string {
  return (
    config.divisionLabels?.[chapterNumber - 1] ??
    `${config.retellingHeadingNoun} ${chapterHeadingNumeral(config, chapterNumber)}`
  );
}

/**
 * Resolve a heading's division label to its chapter number, or `null` when the
 * label names no division of this book.
 *
 * A `divisionLabels` book matches the table exactly — the labels are arbitrary
 * strings, so there is nothing to parse out of them. Otherwise the label must
 * be the book's noun followed by a legal numeral.
 */
export function chapterNumberFromLabel(
  label: string,
  config: BookConfig,
): number | null {
  if (config.divisionLabels) {
    const i = config.divisionLabels.indexOf(label);
    return i === -1 ? null : i + 1;
  }
  const prefix = `${config.retellingHeadingNoun} `;
  if (!label.startsWith(prefix)) return null;
  return chapterNumberFromNumeral(
    label.slice(prefix.length),
    config.retellingHeadingNumeral,
  );
}

/**
 * Parse a retelling Markdown chapter into `{ chapterNumber, title, blocks }`.
 * Paragraph text is preserved verbatim (no inline-Markdown interpretation).
 */
export function parseRetelling(
  md: string,
  config: BookConfig,
): ParsedRetelling {
  const { headingLine, blockSegments, trailingNewline } = splitDocument(md);
  const m = HEADING_RE.exec(headingLine);
  if (!m) {
    throw new Error(
      `retelling heading did not match "# ${divisionLabel(config, 1)}: <title>": ${JSON.stringify(headingLine)}`,
    );
  }
  const label = m[1] ?? "";
  const chapterNumber = chapterNumberFromLabel(label, config);
  if (chapterNumber === null) {
    throw new Error(
      `retelling heading division ${JSON.stringify(label)} names no division ` +
        `of ${config.slug}: ${JSON.stringify(headingLine)}`,
    );
  }
  const title = m[2] ?? "";

  return {
    chapterNumber,
    title,
    blocks: parseBlocks(blockSegments, "retelling"),
    trailingNewline,
  };
}

/**
 * Serialize a parsed retelling back to Markdown — the inverse of
 * `parseRetelling` for everything the model carries.
 *
 * The heading is REGENERATED from `divisionLabel`, not echoed from the source,
 * so a file whose label is stale or non-canonical is rewritten to the label its
 * config defines rather than having the deviation preserved. That is what keeps
 * this agreeing with the DB-regenerated paths, which never saw the source's
 * spelling. The final newline is still the source's: canonical (present) unless
 * the parse saw a file that ended without one.
 */
export function serializeRetelling(
  doc: ParsedRetelling,
  config: BookConfig,
): string {
  return joinDocument(
    `# ${divisionLabel(config, doc.chapterNumber)}: ${doc.title}`,
    doc.blocks,
    doc.trailingNewline,
  );
}

// Re-export so callers importing the round-trip get the block validator and the
// id minter too — both were defined here before `blockMarkdown.ts` existed.
export { blockValidator, blockId };
